import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import React from 'react';

// ── Constants ────────────────────────────────────────────────

export const DAYS_ORDER = [
  'Monday', 'Tuesday', 'Wednesday', 'Thursday',
  'Friday', 'Saturday', 'Sunday',
];

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
const WEEKENDS = ['Saturday', 'Sunday'];

const TODAY_INDEX = new Date().getDay(); // 0=Sun, 1=Mon...
const TODAY_DAY = DAYS_ORDER[TODAY_INDEX === 0 ? 6 : TODAY_INDEX - 1];

export interface DayHours {
  day: string;
  hours: string;
}

export const EMPTY_TRADING_HOURS: DayHours[] = DAYS_ORDER.map((day) => ({
  day,
  hours: '',
}));

// ── Day Type Expander ───────────────────────────────────────

/**
 * Expand a BFF day-type string into the set of DAYS_ORDER entries it covers.
 * Accepts the group values ("All Days", "Weekdays", "Weekends") and
 * individual day names, case-insensitive. Returns an empty array for
 * unrecognised inputs.
 */
const expandCoverageType = (type: string): string[] => {
  switch (type) {
    case 'All Days': return DAYS_ORDER;
    case 'Weekdays': return WEEKDAYS;
    case 'Weekends': return WEEKENDS;
    default: {
      // Case-insensitive match for single day names like "monday" / "MONDAY".
      const normalised = type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
      return DAYS_ORDER.includes(normalised) ? [normalised] : [];
    }
  }
};

// ── Trading Hours Parser ────────────────────────────────────

/**
 * Parse trading hours from the /getparentattributes/trading-hours endpoint.
 * Each item has shape: { value: { type, start, end } } where `type` can be
 * either a single day name ("Monday") or a group value ("All Days",
 * "Weekdays", "Weekends"). Group values fan out to every covered day.
 */
export const parseTradingHours = (
  data: Array<Record<string, unknown>> | undefined,
): DayHours[] | null => {
  if (!data?.length) return null;

  const dayMap: Record<string, string> = {};

  for (const attr of data) {
    const val = attr.value as Record<string, unknown> | undefined;
    if (!val || typeof val !== 'object') continue;

    const dayType = (val.type ?? val.day) as string | undefined;
    if (!dayType) continue;

    const start = (val.start ?? val.openTime ?? val.open ?? '') as string;
    const end = (val.end ?? val.closeTime ?? val.close ?? '') as string;
    const hours = start || end ? `${start} - ${end}`.trim() : '';
    if (!hours) continue;

    for (const day of expandCoverageType(dayType)) {
      if (!dayMap[day]) dayMap[day] = hours;
    }
  }

  if (Object.keys(dayMap).length === 0) return null;

  return DAYS_ORDER.map((day) => ({
    day,
    hours: dayMap[day] || '',
  }));
};

// ── SLA Coverage Parser ─────────────────────────────────────

/**
 * Parse SLA coverage hours from the /getparentattributes/coverages endpoint.
 * Each item has shape: { value: { checked: true, tradingHours: { type, start, end } } }
 */
export const parseCoverageHours = (
  data: Array<Record<string, unknown>> | undefined,
): DayHours[] | null => {
  if (!data?.length) return null;

  const enabled = data.filter((attr) => {
    const val = attr.value as Record<string, unknown> | undefined;
    return val?.checked;
  });

  if (enabled.length === 0) return null;

  const dayMap: Record<string, string> = {};

  for (const attr of enabled) {
    const val = attr.value as Record<string, unknown> | undefined;
    const th = val?.tradingHours as Record<string, string> | undefined;
    if (!th?.type) continue;

    const hours = th.start || th.end ? `${th.start || ''} - ${th.end || ''}`.trim() : '';
    const days = expandCoverageType(th.type);

    for (const day of days) {
      if (!dayMap[day] && hours) dayMap[day] = hours;
    }
  }

  if (Object.keys(dayMap).length === 0) return null;

  return DAYS_ORDER.map((day) => ({
    day,
    hours: dayMap[day] || '',
  }));
};

// ── Display component ────────────────────────────────────────

/** Hours display for a single schedule -- all 7 days, current day highlighted. */
export const HoursDisplay: React.FC<{ days: DayHours[]; emptyText?: string }> = ({
  days,
  emptyText = 'Closed',
}) => (
  <Box>
    {days.map(({ day, hours }) => {
      const isToday = day === TODAY_DAY;
      return (
        <Box
          key={day}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            py: 0.25,
            px: 0.5,
            borderRadius: 0.5,
            bgcolor: isToday ? 'v4.tableSelected' : 'transparent',
            fontWeight: isToday ? 600 : 400,
          }}
        >
          <Typography sx={{ fontSize: '11px', fontWeight: 'inherit', minWidth: 80 }}>
            {day}
          </Typography>
          <Typography
            sx={{
              fontSize: '11px',
              fontWeight: 'inherit',
              color: hours ? 'text.primary' : 'text.secondary',
            }}
          >
            {hours || emptyText}
          </Typography>
        </Box>
      );
    })}
  </Box>
);

/** @deprecated Use HoursDisplay or CombinedHoursDisplay instead */
export const TradingHoursDisplay = HoursDisplay;

/** Combined display — one row per day with Trading Hours and SLA Coverage columns. */
export const CombinedHoursDisplay: React.FC<{
  tradingHours: DayHours[] | null;
  coverageHours: DayHours[] | null;
}> = ({ tradingHours, coverageHours }) => {
  // When no trading hours data exists at all, show a single message instead of
  // "Closed" on every day row which implies hours were configured as closed.
  if (!tradingHours && !coverageHours) {
    return (
      <Typography sx={{ fontSize: '12px', color: 'text.secondary', py: 0.5 }}>
        No trading hours listed for this location
      </Typography>
    );
  }
  const tradingMap: Record<string, string> = {};
  if (tradingHours) for (const { day, hours } of tradingHours) { tradingMap[day] = hours; }
  const coverageMap: Record<string, string> = {};
  if (coverageHours) for (const { day, hours } of coverageHours) { coverageMap[day] = hours; }

  const tradingEmpty = !tradingHours ? 'Not listed' : 'Closed';
  const coverageEmpty = !coverageHours ? 'Not listed' : 'No Coverage';

  return (
    <Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: '80px 1fr 1fr', gap: 1, px: 0.5, pb: 0.5, borderBottom: '1px solid', borderColor: 'v4.border', mb: 0.25 }}>
        <Typography sx={{ fontSize: '10px', fontWeight: 600, color: 'text.secondary' }}>Day</Typography>
        <Typography sx={{ fontSize: '10px', fontWeight: 600, color: 'text.secondary', textAlign: 'right' }}>Trading Hours</Typography>
        <Typography sx={{ fontSize: '10px', fontWeight: 600, color: 'text.secondary', textAlign: 'right' }}>SLA Coverage</Typography>
      </Box>
      {DAYS_ORDER.map((day) => {
        const trading = tradingMap[day] || '';
        const coverage = coverageMap[day] || '';
        const isToday = day === TODAY_DAY;
        return (
          <Box key={day} sx={{ display: 'grid', gridTemplateColumns: '80px 1fr 1fr', gap: 1, py: 0.25, px: 0.5, borderRadius: 0.5, bgcolor: isToday ? 'v4.tableSelected' : 'transparent', fontWeight: isToday ? 600 : 400 }}>
            <Typography sx={{ fontSize: '11px', fontWeight: 'inherit' }}>{day}</Typography>
            <Typography sx={{ fontSize: '11px', fontWeight: 'inherit', textAlign: 'right', color: trading ? 'text.primary' : 'text.secondary' }}>
              {trading || tradingEmpty}
            </Typography>
            <Typography sx={{ fontSize: '11px', fontWeight: 'inherit', textAlign: 'right', color: coverage ? 'text.primary' : 'text.secondary' }}>
              {coverage || coverageEmpty}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};
